import React, { useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ReportingPeriod } from "@/types/ReportingPeriod";
import { HoldingWithInstrumentAndAccount } from "@/types";
import moment from "moment-timezone";

interface CurrencyTrendData {
  period: ReportingPeriod;
  currencyTotals: Record<string, number>;
  date: string;
}

interface CurrencyLineChartProps {
  periodHoldingsData: { period: ReportingPeriod; holdings: HoldingWithInstrumentAndAccount[] }[];
  isLoading: boolean;
}

export const CurrencyLineChart: React.FC<CurrencyLineChartProps> = ({
  periodHoldingsData,
  isLoading,
}) => {
  // Process data to calculate currency totals for each period
  const trendData = useMemo((): CurrencyTrendData[] => {
    return periodHoldingsData
      .map(({ period, holdings }) => {
        const currencyTotals = holdings.reduce((totals, holding) => {
          const currency = holding.currency || "USD";
          if (!totals[currency]) {
            totals[currency] = 0;
          }
          totals[currency] += holding.baseAmount;
          return totals;
        }, {} as Record<string, number>);

        return {
          period,
          currencyTotals,
          date: moment(period.period).format("MMM YYYY"),
        };
      })
      .sort((a, b) => new Date(a.period.period).getTime() - new Date(b.period.period).getTime());
  }, [periodHoldingsData]);

  // Get all unique currencies across all periods
  const allCurrencies = useMemo(() => {
    const currencies = new Set<string>();
    trendData.forEach(data => {
      Object.keys(data.currencyTotals).forEach(currency => currencies.add(currency));
    });
    return Array.from(currencies).sort();
  }, [trendData]);

  // Currency colors
  const currencyColors = useMemo(() => {
    const colors = [
      '#3b82f6', // blue
      '#ef4444', // red
      '#10b981', // emerald
      '#f59e0b', // amber
      '#8b5cf6', // violet
      '#06b6d4', // cyan
      '#f97316', // orange
      '#84cc16', // lime
    ];
    
    const colorMap: Record<string, string> = {};
    allCurrencies.forEach((currency, index) => {
      colorMap[currency] = colors[index % colors.length];
    });
    return colorMap;
  }, [allCurrencies]);

  // Calculate chart dimensions and scales
  const chartDimensions = useMemo(() => {
    if (trendData.length < 2) return null;

    const width = 800;
    const height = 300;
    const padding = { top: 20, right: 60, bottom: 40, left: 60 };
    
    // Find min and max values across all currencies and periods for Y scale
    const allValues = trendData.flatMap(data => Object.values(data.currencyTotals));
    const dataMinValue = Math.min(...allValues);
    const dataMaxValue = Math.max(...allValues);
    
    // Add padding around the data range (10% above and below)
    const range = dataMaxValue - dataMinValue;
    const padding_percent = 0.1;
    const minValue = Math.max(0, dataMinValue - range * padding_percent); // Don't go below 0
    const maxValue = dataMaxValue + range * padding_percent;
    
    return {
      width,
      height,
      padding,
      chartWidth: width - padding.left - padding.right,
      chartHeight: height - padding.top - padding.bottom,
      maxValue,
      minValue
    };
  }, [trendData]);

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(amount);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Currency Trends Chart</CardTitle>
          <CardDescription>Visual representation of currency values over time</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (trendData.length < 2 || !chartDimensions) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Currency Trends Chart</CardTitle>
          <CardDescription>Visual representation of currency values over time</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            Select at least 2 periods to view the line chart
          </div>
        </CardContent>
      </Card>
    );
  }

  const { width, height, padding, chartWidth, chartHeight, maxValue, minValue } = chartDimensions;

  // Create path data for each currency
  const currencyPaths = allCurrencies.map(currency => {
    const points = trendData.map((data, index) => {
      const value = data.currencyTotals[currency] || 0;
      const x = padding.left + (index / (trendData.length - 1)) * chartWidth;
      const y = padding.top + ((maxValue - value) / (maxValue - minValue)) * chartHeight;
      return { x, y, value };
    });

    const pathData = points
      .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
      .join(' ');

    return { currency, pathData, points, color: currencyColors[currency] };
  });

  // Y-axis ticks
  const yTicks = Array.from({ length: 6 }, (_, i) => {
    const value = minValue + (i / 5) * (maxValue - minValue);
    const y = padding.top + ((maxValue - value) / (maxValue - minValue)) * chartHeight;
    return { value, y };
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Currency Trends Chart</CardTitle>
        <CardDescription>
          Visual representation of currency values across {trendData.length} periods
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Legend */}
          <div className="flex flex-wrap gap-3">
            {allCurrencies.map(currency => (
              <div key={currency} className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: currencyColors[currency] }}
                />
                <Badge variant="outline" className="text-xs">
                  {currency}
                </Badge>
              </div>
            ))}
          </div>

          {/* Chart */}
          <div className="w-full overflow-x-auto">
            <svg width={width} height={height} className="border rounded">
              {/* Grid lines */}
              {yTicks.map((tick, index) => (
                <line
                  key={index}
                  x1={padding.left}
                  y1={tick.y}
                  x2={padding.left + chartWidth}
                  y2={tick.y}
                  stroke="#e5e7eb"
                  strokeWidth="1"
                  strokeDasharray="2,2"
                />
              ))}

              {/* X-axis */}
              <line
                x1={padding.left}
                y1={padding.top + chartHeight}
                x2={padding.left + chartWidth}
                y2={padding.top + chartHeight}
                stroke="#6b7280"
                strokeWidth="2"
              />

              {/* Y-axis */}
              <line
                x1={padding.left}
                y1={padding.top}
                x2={padding.left}
                y2={padding.top + chartHeight}
                stroke="#6b7280"
                strokeWidth="2"
              />

              {/* Y-axis labels */}
              {yTicks.map((tick, index) => (
                <text
                  key={index}
                  x={padding.left - 10}
                  y={tick.y + 4}
                  textAnchor="end"
                  fontSize="12"
                  fill="#6b7280"
                >
                  {tick.value > 0 ? formatCurrency(tick.value, 'USD').replace('$', '') : '0'}
                </text>
              ))}

              {/* X-axis labels */}
              {trendData.map((data, index) => {
                const x = padding.left + (index / (trendData.length - 1)) * chartWidth;
                return (
                  <text
                    key={index}
                    x={x}
                    y={padding.top + chartHeight + 20}
                    textAnchor="middle"
                    fontSize="12"
                    fill="#6b7280"
                  >
                    {data.date}
                  </text>
                );
              })}

              {/* Currency lines */}
              {currencyPaths.map(({ currency, pathData, points, color }) => (
                <g key={currency}>
                  {/* Line */}
                  <path
                    d={pathData}
                    fill="none"
                    stroke={color}
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  
                  {/* Data points */}
                  {points.map((point, index) => (
                    <circle
                      key={index}
                      cx={point.x}
                      cy={point.y}
                      r="4"
                      fill={color}
                      stroke="white"
                      strokeWidth="2"
                    >
                      <title>{`${currency}: ${formatCurrency(point.value, currency)} (${trendData[index].date})`}</title>
                    </circle>
                  ))}
                </g>
              ))}
            </svg>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}; 